import { useEffect, useState } from "react";
import { api, formatApiError, IMG } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User, Mail, Phone, MapPin, Monitor, Smartphone, Tablet, Wifi,
  Building2, ShieldCheck, BadgeCheck, XCircle, Star, Heart, Users as UsersIcon,
  CreditCard, Calendar, Clock, Ban, RotateCcw, Globe, KeyRound,
} from "lucide-react";

function DeviceIcon({ type }) {
  if (type === "Mobile") return <Smartphone size={14} />;
  if (type === "Tablet") return <Tablet size={14} />;
  return <Monitor size={14} />;
}

function Row({ label, value, icon, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-3 py-2 text-sm border-b border-brand-subtle last:border-b-0">
      <div className="flex items-center gap-1.5 text-gray-500 w-40 shrink-0">
        {icon}<span className="label-eyebrow">{label}</span>
      </div>
      <div className={`flex-1 min-w-0 break-words text-gray-800 ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-brand-subtle rounded-2xl p-5 mb-4" data-testid={`user-detail-section-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <p className="font-display text-lg brand-forest mb-2">{title}</p>
      <div>{children}</div>
    </div>
  );
}

function PresenceDot({ online, away }) {
  if (online) return <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-bold uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse" />Online now</span>;
  if (away)   return <span className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-bold uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-amber-400" />Away</span>;
  return <span className="inline-flex items-center gap-1.5 text-gray-400 text-xs font-bold uppercase tracking-widest"><span className="w-2 h-2 rounded-full bg-gray-300" />Offline</span>;
}

export default function UserDetailDrawer({ userId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [showBan, setShowBan] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/admin/users/${userId}/full`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const ban = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/ban`, { reason: banReason });
      toast.success("User banned");
      setShowBan(false);
      setBanReason("");
      const { data } = await api.get(`/admin/users/${userId}/full`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const unban = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/unban`, {});
      toast.success("User unbanned");
      const { data } = await api.get(`/admin/users/${userId}/full`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto"
        data-testid="user-detail-drawer"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl brand-forest flex items-center gap-3">
            <User size={22} /> User details
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-16 text-center text-gray-500">Loading…</div>
        ) : (
          <div>
            {/* Header strip */}
            <div className="bg-gradient-to-r from-[#FBF6EC] to-white border border-brand-subtle rounded-2xl p-5 mb-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <PresenceDot online={data.presence.online} away={data.presence.away} />
                    {data.account.banned && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 uppercase tracking-widest">
                        <Ban size={12} /> Banned
                      </span>
                    )}
                  </div>
                  <p className="font-display text-2xl brand-forest truncate">{data.identity.name || "—"}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-1"><Mail size={13} /> {data.identity.email}</p>
                  {data.identity.phone && <p className="text-sm text-gray-600 flex items-center gap-1.5"><Phone size={13} /> {data.identity.phone}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${data.identity.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}`}>
                    {data.identity.role}
                  </span>
                  {data.verification.status === "verified" && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-emerald-600"><BadgeCheck size={12} /> Verified owner</span>
                  )}
                  {data.verification.status === "pending" && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-amber-600"><Clock size={12} /> Verification pending</span>
                  )}
                </div>
              </div>
            </div>

            {/* Identity */}
            <Section title="Identity">
              <Row label="Name" value={data.identity.name} icon={<User size={12} />} />
              <Row label="Email" value={data.identity.email} icon={<Mail size={12} />} mono />
              <Row label="Phone" value={data.identity.phone} icon={<Phone size={12} />} mono />
              <Row label="Referral code" value={data.identity.referral_code} icon={<Star size={12} />} mono />
              <Row label="Referred by" value={data.identity.referred_by_code} icon={<UsersIcon size={12} />} mono />
            </Section>

            {/* Location */}
            <Section title="Location">
              <Row label="Declared city" value={[data.location.declared_city, data.location.declared_state].filter(Boolean).join(", ")} icon={<MapPin size={12} />} />
              <Row label="IP location" value={[data.location.ip_city, data.location.ip_region, data.location.ip_country].filter(Boolean).join(", ")} icon={<Globe size={12} />} />
              <Row label="ISP" value={data.location.isp} icon={<Wifi size={12} />} />
            </Section>

            {/* Device */}
            <Section title="Device & Network">
              <Row label="IP address" value={data.device.last_ip} icon={<Wifi size={12} />} mono />
              <Row label="Device type" value={
                data.device.device_type ? (
                  <span className="inline-flex items-center gap-1.5"><DeviceIcon type={data.device.device_type} /> {data.device.device_type}</span>
                ) : null
              } />
              <Row label="Browser" value={data.device.browser} />
              <Row label="Operating System" value={data.device.os} />
              <Row label="Full User-Agent" value={data.device.raw_user_agent} mono />
            </Section>

            {/* Presence */}
            <Section title="Presence & Sessions">
              <Row label="Status" value={<PresenceDot online={data.presence.online} away={data.presence.away} />} />
              <Row label="Last seen" value={data.presence.last_seen_at && new Date(data.presence.last_seen_at).toLocaleString("en-IN")} icon={<Clock size={12} />} />
              <Row label="Last login" value={data.presence.last_login_at && new Date(data.presence.last_login_at).toLocaleString("en-IN")} icon={<Calendar size={12} />} />
              <Row label="Total logins" value={data.presence.login_count} />
            </Section>

            {/* Verification */}
            <Section title="ID Verification">
              <Row label="Status" value={data.verification.status} />
              <Row label="Submitted" value={data.verification.submitted_at && new Date(data.verification.submitted_at).toLocaleString("en-IN")} />
              <Row label="Reviewed" value={data.verification.reviewed_at && new Date(data.verification.reviewed_at).toLocaleString("en-IN")} />
              <Row label="Rejection reason" value={data.verification.rejection_reason} />
              {data.verification.doc_path && (
                <div className="pt-3">
                  <a href={IMG(data.verification.doc_path)} target="_blank" rel="noreferrer" className="inline-block">
                    <img src={IMG(data.verification.doc_path)} alt="ID document" className="max-w-full max-h-64 object-contain rounded-xl border border-brand-subtle" />
                  </a>
                </div>
              )}
            </Section>

            {/* Activity */}
            <Section title="Activity">
              <Row label="Properties listed" value={`${data.activity.properties_total} total · ${data.activity.properties_approved} live · ${data.activity.properties_pending} pending`} icon={<Building2 size={12} />} />
              <Row label="Contacts revealed" value={data.activity.contacts_revealed} icon={<KeyRound size={12} />} />
              <Row label="Favorites saved" value={data.activity.favorites_saved} icon={<Heart size={12} />} />
              <Row label="Referrals brought" value={data.activity.referrals_brought} icon={<UsersIcon size={12} />} />
              <Row label="Newsletter" value={data.activity.newsletter_subscribed ? "Subscribed" : "Not subscribed"} icon={<Mail size={12} />} />
            </Section>

            {/* Billing */}
            <Section title="Credits & Billing">
              <Row label="Contacts remaining" value={data.billing.contacts_remaining} icon={<CreditCard size={12} />} />
              <Row label="Unlimited until" value={data.billing.unlimited_until && new Date(data.billing.unlimited_until).toLocaleDateString("en-IN")} />
              <Row label="Rewards earned" value={data.billing.rewards_earned} icon={<Star size={12} />} />
            </Section>

            {/* Account */}
            <Section title="Account">
              <Row label="Created" value={data.account.created_at && new Date(data.account.created_at).toLocaleString("en-IN")} icon={<Calendar size={12} />} />
              <Row label="Ban status" value={data.account.banned ? "Banned" : "Active"} />
              <Row label="Ban reason" value={data.account.banned_reason} />
              <Row label="Banned at" value={data.account.banned_at && new Date(data.account.banned_at).toLocaleString("en-IN")} />
            </Section>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2 justify-between">
          {data && data.identity.role !== "admin" && (
            !data.account.banned ? (
              !showBan ? (
                <Button
                  variant="outline"
                  onClick={() => setShowBan(true)}
                  className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-10"
                  data-testid="user-detail-ban-btn"
                ><Ban size={14} className="mr-1.5" /> Ban user</Button>
              ) : (
                <div className="flex gap-2 items-center flex-1 w-full">
                  <Input
                    placeholder="Reason (visible to admin only)"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    className="h-10"
                    data-testid="user-detail-ban-reason"
                  />
                  <Button
                    onClick={ban}
                    disabled={busy}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full h-10"
                    data-testid="user-detail-ban-confirm"
                  >Confirm ban</Button>
                  <Button variant="ghost" onClick={() => { setShowBan(false); setBanReason(""); }} className="h-10">Cancel</Button>
                </div>
              )
            ) : (
              <Button
                variant="outline"
                onClick={unban}
                disabled={busy}
                className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-10"
                data-testid="user-detail-unban-btn"
              ><RotateCcw size={14} className="mr-1.5" /> Unban</Button>
            )
          )}
          <Button variant="ghost" onClick={onClose} className="rounded-full h-10" data-testid="user-detail-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
